import { useState } from "react";
import {
  Card, CardContent, Typography, FormControlLabel, Divider,
  Box, CardMedia, Grid, Button, Switch, TextField,
  InputAdornment, Skeleton, Chip, Tooltip
} from "@mui/material";
import PropTypes from "prop-types";
import moment from "moment";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AddIcon from '@mui/icons-material/Add';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';

import { updateProfileServiceStatus } from "../../actions/profile";
import { isEmpty, urlImg, ColorButton } from "../common/helpers";

import { useDispatch } from "react-redux";

const ServiceEditCard = ({
  type, servicio, borrador = false, irA,
  eliminar
}) => {
  const dispatch = useDispatch();
  const params = new URLSearchParams();
  params.append('service', servicio?._id);

  // URL base de la imagen en Cloudinary
  const getBaseImgUrl = () =>
    !isEmpty(servicio?.img) && Array.isArray(servicio.img) && servicio.img.length > 0
      ? `${urlImg}/servicios/${servicio._id}/${servicio.img[0].nombre}`
      : null;

  const [imgSrc, setImgSrc] = useState(getBaseImgUrl() || '/search.png');
  const [retries, setRetries] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const MAX_RETRIES = 4;

  // Sin useEffect: imgLoaded solo cambia vía onLoad/onError.
  // El useEffect con servicio.img como dependencia reseteaba imgLoaded en cada
  // re-render del padre (servicio.img es array → nueva referencia siempre),
  // dejando el skeleton pegado porque onLoad no se dispara si el src no cambió.

  // Si Cloudinary aún no tiene la imagen disponible, reintenta con backoff (1s, 2s, 3s, 4s)
  const handleImgError = () => {
    const base = getBaseImgUrl();
    if (!base || retries >= MAX_RETRIES) {
      setImgSrc('/search.png');
      setImgLoaded(true);
      return;
    }
    const nextRetry = retries + 1;
    setTimeout(() => {
      setImgSrc(`${base}?r=${nextRetry}`);
      setRetries(nextRetry);
    }, nextRetry * 1000);
  };

  const cambiarStatus = async status => {
    await dispatch(updateProfileServiceStatus({ status }, servicio._id));
  }

  return (
    <Card className="card-edit" sx={{ height: 200 }}>
      <Box sx={{ width: 200, flexShrink: 0, alignSelf: 'stretch', position: 'relative' }}>
        <CardMedia
          component="img"
          sx={{ width: '100%', height: '100%', objectFit: 'cover', visibility: imgLoaded ? 'visible' : 'hidden' }}
          image={imgSrc}
          alt={servicio?.nombre}
          onLoad={() => setImgLoaded(true)}
          onError={handleImgError}
        />
        {!imgLoaded && (
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          />
        )}
        {servicio?.bloqueadoCedula && !borrador && (
          <Tooltip title="Este servicio no será visible ni contratable hasta que tu cédula profesional sea aprobada.">
            <Chip
              icon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 15 }} />}
              label="Pendiente de cédula"
              size="small"
              sx={{
                position: 'absolute', top: 8, left: 8, bgcolor: '#FFF3D6', color: '#92600E',
                fontWeight: 700, height: 24, '& .MuiChip-icon': { color: '#92600E' }
              }}
            />
          </Tooltip>
        )}
      </Box>
      <Box className="container-body" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="subtitle2" fontWeight="bold" color="primary">
            {
              !isEmpty(servicio?.nombre)
                ? servicio?.nombre
                : ('Sin nombre')
            }
          </Typography>
          <Grid container>
            <Grid size={{ xs: 12, md: 9 }}>
              <Typography variant="body2" color="#737791" sx={{
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}>
                {!isEmpty(servicio?.descripcion) ? servicio.descripcion : 'Descripción pendiente'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }} sx={{ textAlign: "end" }}>
              <Typography
                variant="h6"
                color="primary"
                fontWeight="bold"
                sx={{ mt: 1 }}
              >
                {
                  type.includes("SERVICE")
                    ? (!isEmpty(servicio?.costoServicio)) ? (`$ ${servicio.costoServicio}`) : ('Pendiente')
                    : 10
                }
              </Typography>
              <Typography variant="body2" color="#737791">
                {
                  type.includes("SERVICE")
                    ? (!isEmpty(servicio?.tipoCosto)) ? servicio?.tipoCosto : "costo por servicio"
                    : <small>Servicios disponibles</small>
                }
              </Typography>
            </Grid>
          </Grid>
          <Grid
            container
            direction="row"
            sx={{
              justifyContent: "flex-start",
              alignItems: "center",
              paddingBottom: 1,
              minHeight: 28,
            }}
          >
            {!isEmpty(servicio?.ubicacion?.direccion) && (
              <Typography sx={{ marginRight: "0.75rem!important" }} className="text-icon" variant="body2" color="primary">
                <LocationOnIcon sx={{ fontSize: 20, marginRight: 1 }} />
                {servicio?.ubicacion?.direccion}
              </Typography>
            )}
          </Grid>
          <Divider />
          <Grid container sx={{ mt: 'auto', pt: 1 }}>
            <Grid
              size={{ xs: 12, md: 8 }}
              sx={{ display: "flex", alignItems: "center" }}
            >
              {type === "SERVICE_EDIT"
                ? <Box sx={{ flexDirection: "column" }}>
                  <Typography
                    variant="body2"
                    color="primary"
                  >
                    Asigna un facilitador
                  </Typography>
                  <TextField
                    fullWidth
                    sx={{ my: 2 }}
                    slotProps={{
                      input: {
                        startAdornment: <InputAdornment position="start">
                          <Typography color="#737791">@</Typography>
                        </InputAdornment>,
                      },
                    }}
                    size="small"
                  />
                </Box>
                : <Typography variant="body2" color="#737791">
                  Creado el {moment(servicio?.date).format("DD MMMM YYYY")}
                </Typography>
              }
            </Grid>
            <Grid
              size={{ xs: 12, md: 4 }}
              mt={{ xs: 2, md: 0 }}
              className="card-actions"
            >
              {
                (type.includes("SERVICE") && !borrador) && (
                  <Box className="toggle">
                    <Typography variant="body2" color="#737791" sx={{ marginRight: 1 }}>
                      {
                        servicio?.status ? "Activo" : "Inactivo"
                      }
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch checked={servicio?.status} onClick={() => cambiarStatus(!servicio?.status)} name="status" />
                      }
                      label=""
                    />
                  </Box>
                )
              }
              {
                !borrador && (
                  <Button
                    color="success"
                    variant="contained"
                    size="small"
                    endIcon={<InsertLinkIcon />}
                    sx={{ color: "#FFF", minWidth: "auto", marginRight: "0.5rem", fontSize: "14px", textTransform: "none" }}
                    onClick={() => irA(`/user/servicio/${servicio._id}`)}
                  >
                    Ver
                  </Button>
                )
              }
              {
                type !== "SERVICE_EDIT" && (
                  <>
                    <ColorButton
                      variant="contained"
                      size="small"
                      endIcon={<DriveFileRenameOutlineIcon />}
                      sx={{ color: "#FFF", minWidth: "auto", marginRight: "0.5rem", fontSize: "14px", textTransform: "none" }}
                      onClick={() => irA(`/user/crear-servicio/${type === "SERVICE" ? 0 : 1}?${params.toString()}`)}
                    >
                      Editar
                    </ColorButton>
                    <Button
                      color="error"
                      variant="contained"
                      size="small"
                      endIcon={<DeleteIcon />}
                      sx={{ color: "#FFF", minWidth: "auto", marginRight: "0.5rem", fontSize: "14px", textTransform: "none" }}
                      onClick={() => eliminar(servicio?._id)}
                    >
                      Eliminar
                    </Button>
                  </>
                )
              }
              {
                type === "SERVICE_EDIT" && (
                  <Button
                    color="primary"
                    variant="contained"
                    size="small"
                    endIcon={<AddIcon />}
                    sx={{ color: "#FFF", minWidth: "auto", marginRight: "0.5rem", fontSize: "14px", textTransform: "none" }}
                  >
                    Añadir
                  </Button>
                )
              }
            </Grid>
          </Grid>
        </CardContent>
      </Box>
    </Card>
  );
};

ServiceEditCard.propTypes = {
  eliminar: PropTypes.func.isRequired,
  irA: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  servicio: PropTypes.object.isRequired,
  borrador: PropTypes.bool,
}

export default ServiceEditCard;
